/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    Grid, Typography, Select, FormGroup,
    FormControl, MenuItem, TextField, Box, Link, CircularProgress
} from '@material-ui/core'

import {
    ApplicationKeyReducer, ApplicationState, IDPReducer, IDP, Key, ApplicationReducer, OauthToken
} from '../../../../data/interfaces';
import {
    exchangeToken
} from '../../../../data/operations';
import { useStyles } from './style';
import CopyTextField from '../../../../elements/CopyTextField';
import classNames from "classnames";
import CustomIcons from '../../../../icons/CustomIcons';
import { setErrorNotification } from '../../../../store/actions/notification';
import { createOauthTokenSuccess } from "../../../../store/actions/applicationKeys";
import AccessTokenDialog from "../../../shared/AccessTokenDialog/AccessTokenDialog";
import CurlDialog from "../../../shared/CurlDialog/CurlDialog";
import GreyOutlinedButton from '../../../../elements/GreyOutlinedButton';

interface ExternalIDPProps {
    applicationReducer: ApplicationReducer,
    applicationKeysReducer: ApplicationKeyReducer,
    idpReducer: IDPReducer,
    dispatchExchangeToken: (applicationId: string, selectedScopes: string[], key: Key,
        additionalProperties: Record<string, unknown>) => void,
    dispatchSetErrorNotification: (notification: string) => void,
    dispatchCreateOauthTokenSuccess: (token: OauthToken) => void,
}

function ExternalIDP(props: ExternalIDPProps) {
    const classes = useStyles();
    const intl = useIntl();
    const {
        idpReducer, applicationKeysReducer, dispatchExchangeToken, applicationReducer,
        dispatchSetErrorNotification, dispatchCreateOauthTokenSuccess
    } = props;
    const currentKey = applicationKeysReducer?.currentKey;
    const oauthToken = applicationKeysReducer.oauthToken;
    const isTokenGenerating = applicationKeysReducer.isTokenGenerating;
    const isTokenGenerationSuccess = applicationKeysReducer.isTokenGenerationSuccess;
    const isCredentialsAvailable = currentKey !== null && currentKey !== undefined;
    const [externalIDPTokenError, setExternalIDPTokenError] = useState<unknown>('');
    const [externalIDPToken, setExternalIDPToken] = useState('');
    const consumerKey = isCredentialsAvailable ? currentKey?.consumerKey : '';
    const consumerSecret = isCredentialsAvailable ? currentKey?.consumerSecret : '';
    const base64Encoded = window.btoa(consumerKey + ':' + consumerSecret);
    const idpList = idpReducer?.idps.list;
    const [curlOpen, setCurlOpen] = useState(false);
    const [token, setToken] = useState('');
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const applicationId = application?.applicationId;
    const [tokenOpen, setTokenOpen] = useState(false);

    const getExternalIDPs = () => {
        return idpList.filter((idp) => idp.type !== "default" && idp.enabled);
    }

    const externalIDPs = getExternalIDPs();
    
    let initialIDP: IDP;
    if (externalIDPs.length === 1) {
        initialIDP = externalIDPs[0];
    } else {
        initialIDP = {
            id: '',
            name: '',
            type: '',
            revokeEndpoint: '',
            tokenEndpoint: '',
            alias: '',
            enabled: false
        };
    }

    const [selectedIDP, setIDP] = useState<IDP>(initialIDP);
    const [idpError] = useState(false);

    const validateExternalIDPToken = (token: string) => {
        if (token === "") {
            return (
                <FormattedMessage
                    id='application.productionKeys.oAuth.externalIdp.tokenGeneration.token.empty.helperText'
                    defaultMessage={`{selectedIdPType} token cannot be empty`}
                    values={{ selectedIdPType: selectedIDP.type }}

                />
            );
        } {
            return "";
        }
    };

    const handleChangeIDP = (event: any) => {
        idpList.forEach(idp => {
            if (idp.name === event.target.value) {
                setIDP(idp)
            }
        });
    }

    const handleClickTokenOpen = () => {
        setToken('');
        setExternalIDPTokenError(validateExternalIDPToken(externalIDPToken));
        if (externalIDPToken !== "") {
            const additionalProperties = {
                "subject_token": externalIDPToken
            };
            exchangeAccessToken(additionalProperties);
        }
    };

    const handleClickTokenClose = () => {
        setTokenOpen(false);
        dispatchCreateOauthTokenSuccess({ accessToken: '', tokenScopes: [], validityTime: 0 });
    };

    const handleClickCurlOpen = () => {
        setExternalIDPTokenError(validateExternalIDPToken(externalIDPToken));
        if (externalIDPToken != '') {
            setCurlOpen(true);
        }
    };

    const handleClickCurlClose = () => {
        setCurlOpen(false);
    };

    const getTokenGenerateCurlBtnText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oauth.tokenGeneration.curl.buttonText',
            defaultMessage: 'cURL'
        });
    };

    const getTokenGenerateBtnText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oauth.tokenGeneration.buttonText',
            defaultMessage: 'Generate Test Token'
        });
    };

    const onExternalIDPTokenChange = (event: ChangeEvent<HTMLInputElement>) => {
        const externalIDPToken = event.target.value;
        setExternalIDPTokenError(validateExternalIDPToken(externalIDPToken));
        setExternalIDPToken(externalIDPToken);
    };

    const getIdpDocLink = (idpType: string) => {
        if (idpType == "Okta") {
            return "https://developer.okta.com/docs/guides/implement-auth-code/overview/";
        } else if (idpType == "Auth0") {
            return "https://auth0.com/docs/flows/call-your-api-using-the-authorization-code-flow/";
        } else if (idpType == "Microsoft") {
            return "https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-auth-code-flow/";
        } else if (idpType == "Asgardeo") {
            return "https://wso2.com/asgardeo/docs/guides/authentication/oidc/implement-auth-code/";
        } else {
            return "https://wso2.com/choreo/docs"
        }
    }

    useEffect(() => {
        if (applicationKeysReducer.oauthToken) {
            setToken(oauthToken.accessToken);
            setTokenOpen(!isTokenGenerating && isTokenGenerationSuccess && oauthToken.accessToken !== '');
        }
    }, [applicationKeysReducer.oauthToken]);

    const exchangeAccessToken = async (additionalProperties: Record<string, unknown>) => {
        try {
            await dispatchExchangeToken(applicationId as string, [''], currentKey, additionalProperties);
        } catch (error) {
            const errorMessage = intl.formatMessage({
                id: 'application.productionKeys.oAuth.tokenGeneration.error',
                defaultMessage: 'Error occurred during token generation'
            });
            dispatchSetErrorNotification(errorMessage);
        }
    };

    return (
        <main>
            <Grid container spacing={3}>
                <Grid item md={2} className={classes.idpGrid}>
                    <Typography className={classes.keyLabel}>
                        <FormattedMessage
                            id="application.productionKeys.oAuth.externalIdp"
                            defaultMessage="Identity Provider"
                        />
                    </Typography>
                </Grid>
                <Grid item md={2} className={classes.idpGrid}>
                    <FormGroup row>
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            error={idpError}
                            data-testid='external-idp-formcontrol'
                        >
                            <Select
                                value={selectedIDP.name}
                                displayEmpty
                                onChange={handleChangeIDP}
                                variant="outlined"
                                className={classes.selectBox}
                                MenuProps={{
                                    getContentAnchorEl: null,
                                    classes: { paper: classes.dropdownStyle },
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }
                                }}
                                data-testid='idp-select'
                            >
                                <MenuItem value="" disabled>
                                    <em>
                                        <FormattedMessage
                                            id="application.productionKeys.oAuth.externalIdp.select"
                                            defaultMessage="Select an Identity Provider"
                                        />
                                    </em>
                                </MenuItem>
                                {externalIDPs.map((idp: IDP) => (
                                    <MenuItem key={idp.id}
                                        value={idp.name}>
                                        {idp.type + ' | ' + idp.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormGroup>
                </Grid>
                {(selectedIDP.name != '') ?
                    <React.Fragment>
                        <Grid container spacing={3} className={classes.idpGrid} >
                            <Grid item xs={12} className={classes.idpGrid}>
                                <Typography className={classes.subTitle}>
                                    <FormattedMessage
                                        id='application.productionKeys.oAuth.tokenGeneration'
                                        defaultMessage='Token Generation'
                                    />
                                </Typography>
                            </Grid>
                            <Grid item md={12} className={classes.idpGrid}>
                                <Typography className={classes.StepLabel} component='span'>
                                    <FormattedMessage
                                        id="application.productionKeys.oAuth.externalIdp.tokenGeneration.step1"
                                        defaultMessage="Step 1:"
                                    />
                                </Typography>
                                <Box display='inline-block' ml={2}>
                                    <Typography className={classes.StepContent} component='span'>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.externalIdp.tokenGeneration.step1.description'
                                            defaultMessage={`Obtain an access token from {selectedIdpType}`}
                                            values={{ selectedIdpType: selectedIDP.type }}

                                        />
                                    </Typography>
                                </Box>
                                <Box display='block' ml={7} className={classes.removeMarginBottom}>
                                    <Typography className={classNames(classes.noKeyDescription, classes.removeMarginBottom)}>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.externalIdp.tokenGeneration.audience'
                                            defaultMessage='Use the audience value "{allowedAudience}"'
                                            values={{ allowedAudience: selectedIDP.alias }}
                                        />
                                    </Typography>
                                    <Typography className={classNames(classes.noKeyDescription, classes.removeMarginBottom, classes.removeMarginTop)}>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.externalIdp.tokenGeneration.documentation.part1'
                                            defaultMessage='You may refer to the '
                                        />
                                        <Link href={getIdpDocLink(selectedIDP.type)} target="_blank">
                                            <FormattedMessage
                                                id='application.productionKeys.oAuth.externalIdp.tokenGeneration.documentation.part2'
                                                defaultMessage='documentation'
                                            />
                                        </Link>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.externalIdp.tokenGeneration.documentation.part3'
                                            defaultMessage=' for more information'
                                        />
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid container item xs={12} spacing={3}>
                                <Grid item md={2} className={classes.idpGrid}>
                                    <Typography className={classes.keyLabel}>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.tokenEndpoint'
                                            defaultMessage='Token Endpoint'
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item md={6} className={classes.idpGrid}>
                                    <CopyTextField
                                        textContent={selectedIDP?.tokenEndpoint}
                                        disabled={true}
                                        testId="token-endpoint-value"
                                    />
                                </Grid>
                            </Grid>
                            <Grid item md={12} className={classes.idpGrid}>
                                <Typography className={classes.StepLabel} component='span'>
                                    <FormattedMessage
                                        id="application.productionKeys.oAuth.tokenGeneration.step2"
                                        defaultMessage="Step 2:"
                                    />
                                </Typography>
                                <Box display='inline-block' ml={2}>
                                    <Typography className={classes.StepContent} component='span'>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.tokenGeneration.step2.description'
                                            defaultMessage='Obtain test token'
                                        />
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid item md={2} className={classes.idpGrid}>
                                    <Typography
                                        className={classNames(classes.keyLabel)}
                                    >
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.externalIdp.tokenGeneration.token'
                                            defaultMessage={`{selectedIdpType} Token`}
                                            values={{ selectedIdpType: selectedIDP.type }}

                                        />
                                        <span className={classes.error}> *</span>
                                    </Typography>
                                </Grid>
                                <Grid item md={2} className={classes.idpGrid}>
                                    <TextField
                                        onChange={onExternalIDPTokenChange}
                                        size="small"
                                        multiline
                                        rows={2}
                                        value={externalIDPToken}
                                        variant="outlined"
                                        error={externalIDPTokenError !== ""}
                                        FormHelperTextProps={{
                                            className: classes.helperText
                                        }}
                                        helperText={externalIDPTokenError === "" ?
                                            externalIDPTokenError :
                                            <div className={classes.helperTextError}>
                                                <div className={classes.warningIcon}>
                                                    <CustomIcons icon={'warning'} />
                                                </div>
                                                <p> {externalIDPTokenError} </p>
                                            </div>
                                        }
                                        className={classNames(classes.textField, classes.removeMarginTop)}
                                        data-testid='external-idp-token'
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid item md={2} className={classes.idpGrid}>
                                    <Typography
                                        className={classNames(classes.keyLabel)}
                                    >
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.externalIdp.tokenGeneration.testToken'
                                            defaultMessage='Test Token'
                                        />
                                    </Typography>
                                </Grid>
                                <Box display="flex" flexDirection="row" mb={0.7}>
                                    <Box mr={2}>
                                        <GreyOutlinedButton
                                            className={classes.tokenBtnText}
                                            text={getTokenGenerateBtnText()}
                                            onClick={handleClickTokenOpen}
                                            startIcon={isTokenGenerating ? <CircularProgress size={11} color='primary' />
                                                : undefined}
                                            id='generate-choreo-token-btn'
                                        />
                                        {tokenOpen &&
                                            <AccessTokenDialog
                                                tokenOpen={tokenOpen}
                                                token={token}
                                                onClose={handleClickTokenClose}
                                            />
                                        }
                                    </Box>
                                    <Box>
                                        <GreyOutlinedButton
                                            className={classes.tokenBtnText}
                                            text={getTokenGenerateCurlBtnText()}
                                            onClick={handleClickCurlOpen}
                                            id='curl-to-generate-token-btn'
                                            startIcon={<CustomIcons icon="curl" />}
                                        />
                                        {curlOpen &&
                                            <CurlDialog
                                                curlOpen={curlOpen}
                                                base64Encoded={base64Encoded}
                                                externalIDPToken={externalIDPToken}
                                                onClose={handleClickCurlClose}
                                            />
                                        }
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null
                }
            </Grid>
        </main >
    )
}

const mapDispatchToProps = {
    dispatchExchangeToken: exchangeToken,
    dispatchSetErrorNotification: setErrorNotification,
    dispatchCreateOauthTokenSuccess: createOauthTokenSuccess,
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        applicationKeysReducer: state.applicationKeysReducer,
        idpReducer: state.idpReducer,
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(ExternalIDP);
